export type DashboardDatePeriod = 'All' | '7D' | '30D' | '90D' | 'YTD' | 'Custom';

export const DASHBOARD_DATE_PRESETS: DashboardDatePeriod[] = ['All', '7D', '30D', '90D', 'YTD', 'Custom'];

export const isDashboardDateFilterActive = (period: DashboardDatePeriod): boolean => period !== 'All';

/** Non-All presets use day-level event-date aggregates — not month-level revenue APIs. */
export const isDayLevelDashboardPeriod = (period: DashboardDatePeriod): boolean => period !== 'All';

const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getDashboardDateRange = (
  period: DashboardDatePeriod,
  customStart: string,
  customEnd: string,
): { startDate: string; endDate: string } | null => {
  if (period === 'All') {
    return null;
  }

  const end = new Date();
  const start = new Date(end);

  if (period === 'Custom') {
    if (!customStart || !customEnd) {
      return null;
    }
    return { startDate: customStart, endDate: customEnd };
  }

  switch (period) {
    case '7D':
      start.setDate(end.getDate() - 6);
      break;
    case '30D':
      start.setDate(end.getDate() - 29);
      break;
    case '90D':
      start.setDate(end.getDate() - 89);
      break;
    case 'YTD':
      start.setMonth(0, 1);
      break;
    default:
      return null;
  }

  return {
    startDate: formatLocalDate(start),
    endDate: formatLocalDate(end),
  };
};

export const getDashboardDateRangeQueryBounds = (
  customStart: string,
  customEnd: string,
): { startDateTime: string; endDateTime: string } | null => {
  if (!customStart || !customEnd) {
    return null;
  }

  const start = new Date(`${customStart}T00:00:00`);
  const end = new Date(`${customEnd}T23:59:59.999`);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
    return null;
  }

  return {
    startDateTime: start.toISOString(),
    endDateTime: end.toISOString(),
  };
};

export const formatDashboardDateRangeLabel = (
  period: DashboardDatePeriod,
  customStart: string,
  customEnd: string,
): string => {
  if (period === 'All') {
    return 'All time';
  }

  const range = getDashboardDateRange(period, customStart, customEnd);
  if (!range) {
    return period === 'Custom' ? 'Custom range' : period;
  }

  const fmt = (iso: string) =>
    new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  if (period !== 'Custom') {
    return `${period} · ${fmt(range.startDate)} – ${fmt(range.endDate)}`;
  }

  return `${fmt(range.startDate)} – ${fmt(range.endDate)}`;
};

/** Event date within inclusive [start, end]. Used for sold / revenue / delisted / unsold. */
export const isTicketEventInDashboardRange = (
  eventDate: string | null | undefined,
  startDate: string,
  endDate: string,
): boolean => {
  if (!eventDate) {
    return false;
  }
  const day = eventDate.slice(0, 10);
  return day >= startDate && day <= endDate;
};

export const filterTicketsByDashboardEventDate = <T extends {
  e_date?: string | null;
  ticket?: { event?: { e_date?: string | null } | null } | null;
}>(
  rows: T[],
  filterActive: boolean,
  range: { startDate: string; endDate: string } | null,
): T[] => {
  if (!filterActive || !range) {
    return rows;
  }
  return rows.filter((row) => {
    const eventDate = row.e_date ?? row.ticket?.event?.e_date;
    return isTicketEventInDashboardRange(eventDate, range.startDate, range.endDate);
  });
};
/**
 * Filter tickets by sale date (tp_sold_at) instead of event date
 * Used for financial metrics to match Goals page
 */
/**
 * Filter tickets by sale date (tp_sold_at) instead of event date
 * Used for financial metrics to match Goals page logic
 */
export const filterTicketsBySaleDate = <T extends { tp_sold_at?: string | null }>(
  tickets: T[],
  isActive: boolean,
  range: { startDate: string; endDate: string } | null,
): T[] => {
  if (!isActive || !range || !tickets || tickets.length === 0) {
    return tickets;
  }

  const start = new Date(`${range.startDate}T00:00:00`);
  const end = new Date(`${range.endDate}T23:59:59.999`);

  return tickets.filter((ticket) => {
    const saleDate = ticket?.tp_sold_at;
    if (!saleDate) return false;
    const date = new Date(saleDate);
    return date >= start && date <= end;
  });
};

/**
 * Check if a sale date is within the dashboard date range
 */
export const isTicketSaleInDashboardRange = (
  saleDateStr: string | undefined,
  startDate: string | undefined,
  endDate: string | undefined,
): boolean => {
  if (!saleDateStr || !startDate || !endDate) {
    return true;
  }

  const saleDate = new Date(saleDateStr);
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T23:59:59.999`);

  return saleDate >= start && saleDate <= end;
};
